'use client';

import { Xmark } from '@byway/icons';
import clsx from 'clsx';
import NextImage, { type ImageProps } from 'next/image';
import { useTranslations } from 'next-intl';
import { Component, type ReactNode, useState } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { Button } from '~/components/Button';
import { logError } from '~/utils';
import { safeWarn } from '~/utils/logger';
import styles from './Image.module.css';

type Props = Omit<ImageProps, 'style'> & {
  allowZoom?: boolean;
  rounded?: boolean;
  objectFit?: React.CSSProperties['objectFit'];
  label?: string;
  zoomSrc?: string;
};

// Note: in case a non-whitelisted domain is passed and next/image errors
class ImageErrorBoundary extends Component<{ src: string; alt: string; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    logError(error, 'image-remote-pattern');
  }

  render() {
    if (this.state.hasError) {
      return (
        <img src={this.props.src} alt={this.props.alt} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
      );
    }
    return this.props.children;
  }
}

const resolveSrc = (src: ImageProps['src']): string => {
  if (typeof src === 'string') return src;
  if ('src' in src) return src.src; // StaticImport
  return '';
};

/**
 * Wrapper around Next.js Image with optional zoom and rounded variants.
 *
 * **Positioning note:**
 * When `rounded` or `allowZoom` is true, the component renders its own
 * `position: relative` container, so `fill` works out of the box.
 *
 * When neither is set (base path), no container is added — the caller must
 * supply a `position: relative` parent for `fill` to work correctly.
 */
const Image = ({ allowZoom, rounded, label, objectFit = 'cover', zoomSrc, ...props }: Props) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const t = useTranslations('aria');

  if (!props.src) {
    safeWarn('missing src in Image component');
    return null;
  }
  const resolvedSrc = resolveSrc(props.src);

  if (allowZoom || rounded || label) {
    return (
      <>
        <div className={clsx(styles.container, { [styles.rounded]: rounded })}>
          <ImageErrorBoundary src={resolvedSrc} alt={props.alt as string}>
            <NextImage style={{ objectFit }} {...props} />
          </ImageErrorBoundary>
          {label && (
            <div className={styles.label}>
              <span>{label}</span>
            </div>
          )}
          {allowZoom && (
            <button
              type="button"
              className={styles.zoomButton}
              onClick={() => setIsZoomed(true)}
              data-ua-click="expand-image-button"
              data-test-id="image-zoom-button"
              aria-label={t('image_expand')}
            >
              <svg
                className={styles.zoomIcon}
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M7.07906 0C3.17779 0 0 3.17779 0 7.07906C0 10.9803 3.17779 14.1581 7.07906 14.1581C8.77556 14.1581 10.3336 13.5557 11.5546 12.5556L15.7813 16.7823C15.8466 16.8503 15.9247 16.9045 16.0111 16.9419C16.0976 16.9793 16.1906 16.999 16.2848 17C16.379 17.0009 16.4724 16.9831 16.5596 16.9475C16.6468 16.9119 16.726 16.8592 16.7926 16.7926C16.8592 16.726 16.9119 16.6468 16.9475 16.5596C16.9831 16.4724 17.0009 16.379 17 16.2848C16.999 16.1906 16.9793 16.0976 16.9419 16.0111C16.9045 15.9247 16.8503 15.8466 16.7823 15.7813L12.5556 11.5546C13.5557 10.3336 14.1581 8.77556 14.1581 7.07906C14.1581 3.17779 10.9803 0 7.07906 0ZM7.07906 1.41581C10.2152 1.41581 12.7423 3.94295 12.7423 7.07906C12.7423 10.2152 10.2152 12.7423 7.07906 12.7423C3.94295 12.7423 1.41581 10.2152 1.41581 7.07906C1.41581 3.94295 3.94295 1.41581 7.07906 1.41581Z"
                  fill="white"
                />
              </svg>
            </button>
          )}
        </div>
        <ModalOverlay
          isOpen={isZoomed}
          onOpenChange={setIsZoomed}
          isDismissable
          className={styles.overlay}
          data-test-id="image-zoom-modal"
        >
          <Modal className={styles.modal}>
            <div className={styles.closeButtonWrapper}>
              <Button variant="icon" ariaLabel={t('image_expanded_close')} onPress={() => setIsZoomed(false)}>
                <Xmark decorative size="large" />
              </Button>
            </div>
            <Dialog className={styles.dialog} aria-label={t('image_expanded')}>
              <button
                type="button"
                className={styles.zoomedImageWrapper}
                onClick={() => setIsZoomed(false)}
                aria-label={t('image_expanded_close')}
              >
                <NextImage
                  src={zoomSrc ?? props.src}
                  alt={props.alt as string}
                  fill
                  style={{ objectFit: 'cover' }}
                  sizes="90vw"
                />
              </button>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </>
    );
  }

  return (
    <ImageErrorBoundary src={resolvedSrc} alt={props.alt as string}>
      <NextImage style={{ objectFit }} {...props} />
    </ImageErrorBoundary>
  );
};

export { Image };
